import type { AxiosResponse } from "axios";
import ApiService from "src/util/api.service";
import { getLanguageHeaders } from "./commonLanguageHeader";
import { ServicesUrlEndpoints } from "./servicesEndpointConstant";
import type { DeviceMappingRecord, MappingPayload } from "./mappingService";

export interface MappingPresetRecord {
  id: string | number;
  setName?: string;
  name?: string;
  isBase?: boolean;
  deviceMake?: {
    id?: string | number;
    name?: string;
  };
  deviceModel?: {
    id?: string | number;
    name?: string;
  };
}

export interface MappingPresetListParams {
  page?: number;
  size?: number;
  sort?: "ASC" | "DESC";
  sortBy?: string;
}

export interface MappingPresetListResponse {
  presets?: MappingPresetRecord[];
  resources?: MappingPresetRecord[];
  totalItems?: number;
  totalElements?: number;
  total?: number;
  size?: number;
  page?: number;
}

export interface MappingPresetNamesRecord {
  deviceType?: string | number;
  setNames?: string[];
}

export interface MappingPresetPropertyRecord {
  packetCode?: string;
  propertyName?: string;
  value?: unknown;
  lastValue?: unknown;
  currentValue?: unknown;
}

export type MappingPresetPayload = Omit<MappingPayload, "unitId"> & {
  presetId?: string | number;
  deviceType?: string | number;
  base?: boolean;
};

export type UpdateMappingPresetPayload = Omit<MappingPayload, "unitId"> & {
  deviceType?: string | number;
  isPreset: true;
};

const withParams = (url: string, params: Record<string, unknown>) => {
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, String(value));
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `${url}?${queryString}` : url;
};

const replacePresetId = (url: string, presetId: string | number) =>
  url.replace("{presetId}", String(presetId));

export const getMappingPresets = (
  params: MappingPresetListParams = {},
  language?: string
): Promise<AxiosResponse<{ response: MappingPresetListResponse }>> =>
  ApiService.get(
    withParams(ServicesUrlEndpoints.MAPPING_PRESETS.LISTING, { ...params }),
    getLanguageHeaders(language)
  );

export const getMappingPresetById = (presetId: string | number, language?: string) =>
  ApiService.get<{ response: DeviceMappingRecord[] }>(
    replacePresetId(ServicesUrlEndpoints.MAPPING_PRESETS.LISTING_BY_ID, presetId),
    getLanguageHeaders(language)
  );

export const getMappingPresetByDeviceType = (
  deviceTypeId?: string | number,
  language?: string
) =>
  ApiService.get<{ response: MappingPresetRecord[] }>(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.LISTING_BY_DEVICE_TYPE}/${deviceTypeId}`,
    getLanguageHeaders(language)
  );

export const getMappingPresetNames = (language?: string) =>
  ApiService.get<{ response: MappingPresetNamesRecord[] }>(
    ServicesUrlEndpoints.MAPPING_PRESETS.PRESET_NAMES,
    getLanguageHeaders(language)
  );

export const getMappingPresetProperties = (presetId: string | number, language?: string) =>
  ApiService.get<{ response: MappingPresetPropertyRecord[] }>(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.PROPERTY}/${presetId}`,
    getLanguageHeaders(language)
  );

export const cloneMappingPreset = (
  params: { id: string | number; name: string },
  language?: string
) =>
  ApiService.post(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.CLONE_PRESET}/${params.id}?name=${encodeURIComponent(
      params.name
    )}`,
    undefined,
    getLanguageHeaders(language)
  );

export const applyMappingPreset = (
  params: { unitId: string | number; presetId: string | number },
  language?: string
) =>
  ApiService.post(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.USE_PRESET}/${params.unitId}/preset/${params.presetId}`,
    undefined,
    getLanguageHeaders(language)
  );

export const exportMappingPreset = (
  params: { deviceMappingId: string | number; name: string },
  language?: string
) =>
  ApiService.post(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.EXPORT_PRESET}/${params.deviceMappingId}/${encodeURIComponent(
      params.name
    )}`,
    undefined,
    getLanguageHeaders(language)
  );

export const createMappingPresetMapping = (payload: MappingPresetPayload, language?: string) =>
  ApiService.post(
    ServicesUrlEndpoints.MAPPING_PRESETS.CREATE_MAPPING_PRESET,
    payload,
    getLanguageHeaders(language)
  );

export const updateMappingPresetMapping = (
  params: {
    id: string | number;
    presetId: string | number;
    payload: UpdateMappingPresetPayload;
  },
  language?: string
) =>
  ApiService.patch(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.UPDATE_MAPPING_PRESET}/${params.id}?presetId=${params.presetId}`,
    params.payload,
    getLanguageHeaders(language)
  );

export const deleteMappingPresetMapping = (
  params: { mappingId: string | number; presetId: string | number },
  language?: string
) =>
  ApiService.delete(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.DELETE_MAPPING_PRESET}/${params.mappingId}?presetId=${params.presetId}`,
    getLanguageHeaders(language)
  );

export const deleteMappingPreset = (presetId: string | number, language?: string) =>
  ApiService.delete(
    `${ServicesUrlEndpoints.MAPPING_PRESETS.DELETE}/${presetId}`,
    getLanguageHeaders(language)
  );
